import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Observable, Subscription, take, fromEvent, merge, timer, of, Subject } from 'rxjs';
import { throttleTime, switchMap, mapTo } from 'rxjs/operators';
import * as Sentry from "@sentry/angular";

import { environment } from '../../../../environments/environment';
import { ContentService } from '../content/content.service';
import { AppidService } from '../appid/appid.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private appID: number;

    private readonly timeToLogoutDueToInactivity = 30 * 60 * 1000;
    
	// Subscription to track inactivity timer.
	private inactivitySubscription: Subscription | null = null; 
    private activity$ = merge(
        fromEvent(window, 'mousemove'),
        fromEvent(window, 'mousedown'),
        fromEvent(window, 'keydown'),
        fromEvent(window, 'touchstart'),
        fromEvent(window, 'scroll'),
        fromEvent(window, 'click')
    ).pipe(throttleTime(3 * 60 * 1000)); // check activity at most every 3 minutes.

	// Subject to emit PageActionResponses.
    public actionResponse$: Subject<any> = new Subject<any>(); 

    constructor(private http: HttpClient, private appidService: AppidService, private ngZone: NgZone) {

        this.appidService.appID$.pipe(take(1)).subscribe((appID) => {
            this.appID = appID;
        });

        if (localStorage.getItem('token')) {
            this.isLoggedIn = true;
            this.checkUserActivity();
        }
    }

    public isLoggedIn: boolean = false;
    private token: string;
    private role: number;
    private org: number;

    registerOrg(orgName: string, role: string): Observable<any> {
        return new Observable((observer) => {
            this.http.post(environment.urls.core + '/auth/registerorg', {
                OrgName: orgName,
                RoleID: parseInt(role)
            }).subscribe(
                (response) => {
                    observer.next(response);
                },
                (error) => {
                    observer.error(error);
                }
            )
        });
    }

    getRole(): number {
        if (this.role == null) this.role = parseInt(localStorage.getItem("role"));
        return this.role;
    }

    getOrg(): number {
        if (this.org == null) {
            this.org = parseInt(localStorage.getItem("org") || localStorage.getItem("V1OrgID"));
        }
        return this.org;
    }

    getToken(): string {
        if (this.token == null) this.token = localStorage.getItem("token");
        return this.token;
    }

    saveToken(token) {
        localStorage.setItem("token", token);
        this.token = token;
    }

    setOrgAndRole(org: number, role: number): void {
        this.org = org;
        this.role = role;
        localStorage.setItem("org", this.org.toString());
        localStorage.setItem("role", this.role.toString());
    }

    logout(): void {
        this.token = '';
        this.role = null;
        this.isLoggedIn = false;

        localStorage.removeItem("token");
        localStorage.removeItem("role");
        localStorage.removeItem("IndividualPersonID");

        sessionStorage.removeItem("isInWorkflow");

        Sentry.getCurrentScope().clear();

        // stop idle tracking on logout.
        this.stopCheckUserActivity();
    }

    // Start idle tracking: if no activity for timeToLogoutDueToInactivity => logout().
    checkUserActivity(): void {
        this.stopCheckUserActivity();

        // Run outside Angular to avoid triggering change detection on every user activity.
        this.ngZone.runOutsideAngular(() => {
        
            const idle$ = merge(of(true), this.activity$).pipe(

                // Reset the timer every time there is activity
                switchMap(() => timer(this.timeToLogoutDueToInactivity))
            );

            this.inactivitySubscription = idle$.subscribe({
                next: () => {

                    // Once the idle$ timer runs out, send a request to the backend to get the correct logout info,
					//  then emit a response to the actionResponse$ subject.
                    this.ngZone.run(() => {
                        let role_id = localStorage.getItem('role')
                        let httpParams = new HttpParams().set('role_id', role_id);

                        this.http.post(environment.urls.core + '/auth/timeout', {}, {
                            params: httpParams,
                            headers: { 'authorization': this.getToken() }

                        }).pipe(take(1)).subscribe({
                            next: (resp: any) => {
                                this.actionResponse$.next(resp);
                            },
                            error: (err) => {
                                console.error('[Idle] timeout endpoint error', err);
                            }
                        });
                    });
                },
                error: (error) => {
                    console.error('[Idle] Error in idle subscription:', error);
                }
            });
        });
    }

	// stop idle tracking on logout.
    stopCheckUserActivity(): void {
        this.inactivitySubscription?.unsubscribe();
        this.inactivitySubscription = null;
    }

    changeOrg(orgID): Observable<any> {
        return new Observable((observer) => {
            let httpParams = new HttpParams()
                .set('app_id', this.appID)
                .set('org_id', this.getOrg())
                .set('newOrgID', orgID);

            this.http.get(environment.urls.core + '/auth/changeorg', {
                params: httpParams,
                headers: {
                    'authorization': this.getToken()
                }
            }).subscribe(
                (data: any) => {
                    this.setOrgAndRole(data.newOrg, data.newRole);
                    this.saveToken(data.token);
                    observer.next(data);
                },
                (error: any) => {
                    observer.error(error);
                    console.error(error);
                }
            );
        });
    }

    // When is V2->V2 app change, no tested yet.
    changeApp(appID: number, navID: number, newAppID: number, NavSourceV1?: string, V1SID?: string, V1OrgID?: string, V1PageID?: string, V1UserID?: string): Observable<any> {
        return new Observable((observer) => {
            let httpParams = new HttpParams()
                .set('app_id', appID)
                .set('nav_id', navID)
                .set('org_id', this.getOrg())
                .set('newAppID', newAppID.toString());

            if (NavSourceV1) {
                httpParams = httpParams.set('NavSourceV1', NavSourceV1).set('sid', V1SID).set('org_id', V1OrgID).set('page_id', V1PageID).set('V1UserID', V1UserID).set('fromV1', 'true');
            }

            this.http.get(environment.urls.core + '/auth/changeApp', {
                params: httpParams,
                headers: {
                    
                    // When is V2 user no tested yet.
                    // Unknown person used for unuath role, or here it works when the user comes from V1.
                    'authorization': this.getToken() ? this.getToken() : 'unknown person'
                }
            }).subscribe(
                (data: any) => {                    
                    observer.next(data);
                },
                (error: any) => {
                    console.error('Error changing app:', error);
                }
            );
        });
    }
}
